import { mergeConfig, validateConfig, TRACKING_CONFIG } from './config'

import type {
  CreateGATrackerOptions,
  GAEventData,
  GATrackingOptions,
  IGATrackingEngine,
} from './types'

const NEW_SITE_EVENTS = new Set(['page_view', 'add_payment_info', 'purchase_failed'])

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

function finiteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function sanitizeEcommerce(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  const ecommerce = value as Record<string, unknown>
  const currency = nonEmptyString(ecommerce.currency)?.toUpperCase()
  const total = finiteNumber(ecommerce.value)
  const paymentType = nonEmptyString(ecommerce.payment_type)
  const items = Array.isArray(ecommerce.items)
    ? ecommerce.items.flatMap((value) => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) return []
        const item = value as Record<string, unknown>
        const itemId = nonEmptyString(item.item_id)
        const itemName = nonEmptyString(item.item_name)
        const itemCategory = nonEmptyString(item.item_category)
        const itemVariant = nonEmptyString(item.item_variant)
        const price = finiteNumber(item.price)
        const quantity = finiteNumber(item.quantity)
        if (
          !itemId ||
          !itemName ||
          !itemCategory ||
          price === undefined ||
          price < 0 ||
          quantity === undefined ||
          quantity <= 0
        )
          return []
        return [
          {
            item_id: itemId,
            item_name: itemName,
            item_category: itemCategory,
            ...(itemVariant ? { item_variant: itemVariant } : {}),
            price,
            quantity,
          },
        ]
      })
    : []
  if (
    !currency ||
    !/^[A-Z]{3}$/.test(currency) ||
    total === undefined ||
    total < 0 ||
    items.length === 0
  )
    return undefined
  return {
    currency,
    value: total,
    ...(paymentType ? { payment_type: paymentType } : {}),
    items,
  }
}

function sanitizeNewSiteEvent(data: GAEventData): GAEventData | null {
  const event = data.event ?? ''
  if (!NEW_SITE_EVENTS.has(event)) return null
  if (event === 'page_view') {
    const pagePath = nonEmptyString(data.page_path)
    const pageTitle = nonEmptyString(data.page_title)
    return {
      event,
      ...(pagePath ? { page_path: pagePath } : {}),
      ...(pageTitle ? { page_title: pageTitle } : {}),
    }
  }
  if (event === 'purchase_failed') {
    const paymentType = nonEmptyString(data.payment_type)
    const errorCode = nonEmptyString(data.error_code)
    return paymentType && errorCode
      ? { event, payment_type: paymentType, error_code: errorCode }
      : null
  }
  const paymentType = nonEmptyString(data.payment_type)
  const ecommerce = sanitizeEcommerce(data.ecommerce)
  return paymentType && ecommerce ? { event, payment_type: paymentType, ecommerce } : null
}

/** 全局单例引擎实例（防止 React StrictMode 导致重复初始化） */
let globalEngineInstance: GATrackingEngine | null = null

/**
 * GA 追踪引擎核心实现
 * @description 与 vigilkids-frontend 完全一致的追踪逻辑
 */
export class GATrackingEngine implements IGATrackingEngine {
  private options: Required<GATrackingOptions>
  private observer?: MutationObserver
  private isInitialized = false
  private processedClicks: Record<string, number> = {}
  private dataLayerRef?: unknown[]
  private eventListeners: Array<{
    element: Element | Document
    event: string
    handler: EventListener
  }> = []

  constructor(options: GATrackingOptions = {}, context?: CreateGATrackerOptions['context']) {
    if (!validateConfig(options)) {
      throw new Error('[GA Tracking] Invalid configuration')
    }

    this.options = mergeConfig(options)
    this.dataLayerRef = context?.dataLayer as unknown[]

    // dataLayer 初始化不涉及 DOM，可在构造时安全调用
    // 确保 track() 在 init() 之前也能正常推送事件
    this.initDataLayer()

    if (this.options.debug) {
      this.log('初始化点击追踪脚本 (PayKit 版)')
    }
  }

  private log(...args: unknown[]): void {
    const hasWindow = typeof window !== 'undefined'
    if (this.options.debug && hasWindow && window.console) {
      console.log('[GA Tracking]', ...args)
    }
  }

  private handleError(context: string, error: unknown, fallback?: unknown): unknown {
    if (this.options.debug) {
      this.log(`${context}错误:`, error)
    }
    return fallback
  }

  private addEventListenerWithTracking(
    element: Element | Document,
    event: string,
    handler: EventListener,
    options?: boolean | AddEventListenerOptions
  ): void {
    element.addEventListener(event, handler, options)
    this.eventListeners.push({ element, event, handler })
  }

  private removeAllEventListeners(): void {
    this.eventListeners.forEach(({ element, event, handler }) => {
      try {
        element.removeEventListener(event, handler)
      } catch (e) {
        this.handleError('移除事件监听器', e)
      }
    })
    this.eventListeners = []
  }

  /**
   * 初始化追踪引擎
   */
  init(): void {
    if (this.isInitialized) {
      if (this.options.debug) {
        this.log('Engine already initialized')
      }
      return
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      this.log('Cannot initialize in non-browser environment')
      return
    }

    this.initDataLayer()
    this.setupClickTracking()
    this.setupLinkTracking()
    this.setupMutationObserver()

    this.isInitialized = true

    if (this.options.debug) {
      this.log('Engine initialized successfully')
    }
  }

  /**
   * 销毁追踪引擎
   */
  destroy(): void {
    if (!this.isInitialized) return

    this.removeAllEventListeners()

    if (this.observer) {
      this.observer.disconnect()
      this.observer = undefined
    }

    this.processedClicks = {}
    this.isInitialized = false

    if (this.options.debug) {
      this.log('Engine destroyed')
    }
  }

  /**
   * 手动发送追踪数据
   */
  track(eventData: GAEventData): void {
    const enrichedData = { ...eventData }

    if (enrichedData.module) {
      enrichedData.module = this.applyModulePrefix(enrichedData.module)
    }

    this.sendToDataLayer(enrichedData)
  }

  /**
   * 更新配置
   */
  updateOptions(newOptions: Partial<GATrackingOptions>): void {
    Object.assign(this.options, newOptions)

    if (this.options.debug) {
      this.log('Configuration updated:', this.options)
    }
  }

  /**
   * 获取当前配置
   */
  getOptions(): GATrackingOptions {
    return { ...this.options }
  }

  /**
   * 检查元素是否应该被追踪
   */
  shouldTrackElement(element: HTMLElement): boolean {
    if (this.isInBlacklist(element)) {
      return false
    }
    return this.isInWhitelist(element)
  }

  /**
   * 获取元素的 GA 数据
   */
  getGAData(element: HTMLElement): GAEventData | null {
    const customData = this.getCustomDataAttributes(element)
    const elementText = (customData.action_name as string) || this.safeGetElementText(element)
    const elementId = (customData.element_id as string) || element.id || ''
    const elementClasses =
      (customData.element_classes as string) ||
      (element.className && typeof element.className === 'string' ? element.className : '')
    const rawModule = (customData.module as string) || this.identifyModule(element)
    const module = this.applyModulePrefix(rawModule)
    const interactionType =
      (customData.user_interaction_type as string) || this.getInteractionType(element)
    const deviceCategory =
      (customData.device_category as 'mobile' | 'tablet' | 'desktop') || this.getDeviceCategory()

    const eventData: GAEventData = {
      event: 'click_interaction',
      user_interaction_type: interactionType + '_click',
      action_name: elementText || 'Unknown_Text',
      module: module,
      element_id: elementId,
      element_classes: elementClasses,
      device_category: deviceCategory,
    }

    for (const key in customData) {
      if (
        Object.prototype.hasOwnProperty.call(customData, key) &&
        !Object.prototype.hasOwnProperty.call(eventData, key)
      ) {
        eventData[key] = customData[key]
      }
    }

    return eventData
  }

  private initDataLayer(): void {
    if (this.dataLayerRef) {
      return
    }

    if (typeof window === 'undefined') {
      return
    }

    const win = window as unknown as { dataLayer?: unknown[] }
    const existingLayer = win.dataLayer

    if (Array.isArray(existingLayer)) {
      this.dataLayerRef = existingLayer
      return
    }

    const initializedLayer: unknown[] = []
    win.dataLayer = initializedLayer
    this.dataLayerRef = initializedLayer
  }

  private getDeviceCategory(): 'mobile' | 'tablet' | 'desktop' {
    if (typeof window === 'undefined') {
      return 'desktop'
    }

    const width = window.innerWidth
    if (width <= TRACKING_CONFIG.MOBILE_BREAKPOINT) {
      return 'mobile'
    } else if (width <= TRACKING_CONFIG.TABLET_BREAKPOINT) {
      return 'tablet'
    } else {
      return 'desktop'
    }
  }

  private cleanText(text: string): string {
    if (!text) return ''
    return text.replace(/\s+/g, ' ').trim()
  }

  private safeMatchesSelector(element: Element, selector: string): boolean {
    if (!element || !selector) return false
    try {
      return element.matches(selector)
    } catch {
      return false
    }
  }

  private isInWhitelist(element: Element): boolean {
    const whitelist = this.options.whitelist
    if (!whitelist || !whitelist.length) return true
    try {
      if (
        element.hasAttribute &&
        (element.hasAttribute('data-ga-track') || element.hasAttribute('data-ga-action-name'))
      ) {
        if (element.getAttribute('data-ga-track') === 'false') return false
        return true
      }
      for (const selector of whitelist) {
        if (selector && this.safeMatchesSelector(element, selector)) {
          return true
        }
      }
    } catch (e) {
      return this.handleError('检查白名单', e, true) as boolean
    }
    return false
  }

  private isInBlacklist(element: Element): boolean {
    const blacklist = this.options.blacklist
    if (!blacklist || !blacklist.length) return false
    try {
      for (const selector of blacklist) {
        if (selector && this.safeMatchesSelector(element, selector)) {
          return true
        }
      }
    } catch (e) {
      return this.handleError('检查黑名单', e, false) as boolean
    }
    return false
  }

  private setupClickTracking(): void {
    const handleClick = (event: Event) => {
      try {
        const target = event.target as HTMLElement
        if (!target) return

        // 先找到交互元素
        let interactiveElement: HTMLElement | null = target
        let found = false

        for (let j = 0; j < TRACKING_CONFIG.MAX_DEPTH_SEARCH && interactiveElement; j++) {
          if (this.isInWhitelist(interactiveElement) && !this.isInBlacklist(interactiveElement)) {
            found = true
            break
          }
          interactiveElement = interactiveElement.parentElement
        }

        if (!found || !interactiveElement) return

        // 使用交互元素生成 clickId（而不是原始 target），防止点击子元素时重复触发
        const timestamp = Date.now()
        const clickId = this.generateClickId(interactiveElement, timestamp)
        if (this.processedClicks[clickId]) return
        this.processedClicks[clickId] = timestamp

        const eventData = this.getGAData(interactiveElement)
        if (!eventData) return

        this.log('追踪点击事件:', eventData)
        this.sendToDataLayer(eventData)
      } catch (e) {
        this.handleError('处理点击事件', e)
      }
    }

    this.addEventListenerWithTracking(document, 'click', handleClick, true)
  }

  private safeGetElementText(element: HTMLElement): string {
    if (!element) return ''
    try {
      let text = (element.innerText || element.textContent || '').trim()
      if (!text) {
        text = (element.getAttribute('aria-label') || element.getAttribute('title') || '').trim()
      }
      if (!text && element.querySelector('img')) {
        const img = element.querySelector('img') as HTMLImageElement
        text = (img.alt || '').trim()
      }
      return this.cleanText(text)
    } catch {
      return ''
    }
  }

  private identifyModule(element: Element): string {
    if (!element) return 'unknown'

    try {
      let currentElement: Element | null = element
      let depth = 0
      const moduleRules = this.options.moduleRules ?? []

      while (currentElement && depth < TRACKING_CONFIG.MODULE_DETECTION_DEPTH) {
        for (const rule of moduleRules) {
          if (this.safeMatchesSelector(currentElement, rule.selector)) {
            if (rule.module === 'dynamic' && rule.handler) {
              return rule.handler(currentElement)
            }
            return rule.module
          }
        }
        currentElement = currentElement.parentElement
        depth++
      }

      return 'unknown'
    } catch (error) {
      return this.handleError('识别模块', error, 'unknown') as string
    }
  }

  private getInteractionType(element: HTMLElement): string {
    if (!element) return 'click'
    try {
      if (element.tagName === 'A') return 'link'
      if (element.tagName === 'BUTTON' || element.matches('button')) return 'button'
      if (element.matches('[type="radio"]')) return 'radio'
      if (element.matches('[type="checkbox"]')) return 'checkbox'
      if (element.getAttribute('role') === 'button') return 'button'
    } catch {
      // ignore
    }
    return 'click'
  }

  private getCustomDataAttributes(element: Element): Record<string, unknown> {
    if (!element) return {}
    const customData: Record<string, unknown> = {}
    try {
      const attributes = element.attributes
      for (let i = 0; i < attributes.length; i++) {
        const attr = attributes.item(i)
        if (attr && attr.name.startsWith('data-ga-')) {
          const dataLayerKey = attr.name.replace('data-ga-', '').replace(/-/g, '_')
          if (this.isObjectString(attr.value)) {
            customData[dataLayerKey] = JSON.parse(attr.value)
          } else {
            customData[dataLayerKey] = attr.value
          }
        }
      }
    } catch {
      // ignore
    }
    return customData
  }

  private generateClickId(element: HTMLElement, timestamp: number): string {
    try {
      let elementInfo = element.tagName || ''
      elementInfo +=
        element.className && typeof element.className === 'string' ? element.className : ''
      // 使用 100ms 时间窗口进行去重，避免同一点击在不同毫秒触发多次
      const timeWindow = Math.floor(timestamp / 100)
      return elementInfo + '_' + timeWindow
    } catch {
      return 'click_' + Math.floor(timestamp / 100)
    }
  }

  private applyModulePrefix(module: string): string {
    if (!module || !this.options.modulePrefix) {
      return module
    }

    if (module.startsWith(this.options.modulePrefix)) {
      return module
    }

    return this.options.modulePrefix + module
  }

  private setupMutationObserver(): void {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.processLinks(node as HTMLElement)
          }
        })
      })
    })

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
    })
  }

  private sendToDataLayer(data: GAEventData): void {
    let eventData = data
    if (this.options.businessEventContract === 'ga4_ecommerce_v1') {
      const sanitized = sanitizeNewSiteEvent(data)
      if (!sanitized) return
      eventData = sanitized
    }
    if (this.options.debug) {
      this.log('发送到 dataLayer:', eventData)
    }

    const win =
      typeof window !== 'undefined' ? (window as unknown as { dataLayer?: unknown[] }) : undefined
    const dataLayer = this.dataLayerRef || win?.dataLayer

    try {
      if (Array.isArray(dataLayer)) {
        if (this.options.businessEventContract === 'ga4_ecommerce_v1' && eventData.ecommerce) {
          dataLayer.push({ ecommerce: null })
        }
        dataLayer.push(eventData)
        this.dataLayerRef = dataLayer
      }
    } catch {
      if (this.options.debug) this.log('发送分析事件错误')
    }
  }

  private setupLinkTracking(): void {
    if (this.options.debug) {
      this.log('初始化链接追踪脚本')
    }

    this.processLinks(document)

    const handleLinkClick = (event: Event) => {
      try {
        const target = event.target as HTMLElement
        if (!target) return

        let linkElement: HTMLElement | null = target
        while (linkElement && linkElement.tagName !== 'A') {
          linkElement = linkElement.parentElement
          if (!linkElement) return
        }

        const link = linkElement as HTMLAnchorElement
        if (!link.href) return

        const linkType = this.getLinkType(link.href)

        const eventData: GAEventData = {
          event: 'link_interaction',
          user_interaction_type: 'link_click',
          action_name: this.safeGetElementText(link) || link.href,
          module: this.applyModulePrefix(this.identifyModule(link)),
          element_id: link.id || '',
          element_classes:
            link.className && typeof link.className === 'string' ? link.className : '',
          device_category: this.getDeviceCategory(),
          link_url: link.href,
          link_type: linkType,
        }

        this.log('追踪链接点击:', eventData)
        this.sendToDataLayer(eventData)
      } catch (e) {
        this.handleError('处理链接点击', e)
      }
    }

    this.addEventListenerWithTracking(document, 'click', handleLinkClick, true)
  }

  private processLinks(container?: Document | HTMLElement): void {
    try {
      const searchRoot = container || document
      const links = searchRoot.querySelectorAll('a[href]')

      for (let i = 0; i < links.length; i++) {
        const link = links.item(i)
        if (!link) continue
        if (link.hasAttribute('data-tracking-processed')) continue
        if (!this.isInWhitelist(link) || this.isInBlacklist(link)) {
          link.setAttribute('data-tracking-processed', 'filtered')
          continue
        }

        const href = link.getAttribute('href')
        const customData = this.getCustomDataAttributes(link)
        const linkType = (customData.link_type as string) || this.getLinkType(href)
        const rawModule = (customData.module as string) || this.identifyModule(link)
        const module = this.applyModulePrefix(rawModule)
        const actionName =
          (customData.action_name as string) || this.cleanText((link as HTMLElement).innerText)
        const pageSource =
          (customData.page_source as string) ||
          module + '|' + actionName.replace(/\s+/g, '_').toLowerCase()

        link.setAttribute('data-tracking-processed', 'true')
        link.setAttribute('data-tracking-module', module)
        link.setAttribute('data-tracking-action', actionName)
        link.setAttribute('data-tracking-type', linkType)

        if (!this.isTrackOnlyType(linkType) && !this.hasPageSourceParam(href)) {
          const newHref = this.addUrlParameter(href, 'page_source', pageSource)
          link.setAttribute('href', newHref)
        }
      }
    } catch (e) {
      this.log('处理链接错误:', e)
    }
  }

  private getLinkType(href: string | null): string {
    if (!href) return 'unknown'
    try {
      if (href.startsWith('mailto:')) return 'mailto'
      if (href.startsWith('tel:')) return 'tel'
      if (href.startsWith('sms:')) return 'sms'
      if (href.startsWith('javascript:')) return 'javascript'
      if (href.startsWith('#')) return 'anchor'

      const tempA = document.createElement('a')
      tempA.href = href
      if (!tempA.hostname) return 'internal'

      let isInternal = false
      for (let i = 0; i < this.options.internalDomains.length; i++) {
        if (
          tempA.hostname === this.options.internalDomains[i] ||
          tempA.hostname.endsWith('.' + this.options.internalDomains[i])
        ) {
          isInternal = true
          break
        }
      }
      return isInternal ? 'internal' : 'external'
    } catch {
      return 'unknown'
    }
  }

  private isTrackOnlyType(linkType: string): boolean {
    return this.options.trackOnlyTypes.includes(linkType)
  }

  private hasPageSourceParam(url: string | null): boolean {
    return url ? url.includes('page_source=') : false
  }

  private isObjectString(value: string): boolean {
    if (
      !value ||
      typeof value !== 'string' ||
      !value.trim().startsWith('{') ||
      !value.trim().endsWith('}')
    ) {
      return false
    }

    try {
      const parsed: unknown = JSON.parse(value)
      return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
    } catch {
      return false
    }
  }

  private addUrlParameter(url: string | null, paramName: string, paramValue: string): string {
    if (!url || !paramName || !paramValue) return url || ''
    const anchor = url.split('#')
    const baseUrl = anchor[0] ?? ''
    const hash = anchor.length > 1 ? '#' + anchor[1] : ''
    const separator = baseUrl.includes('?') ? '&' : '?'
    return (
      baseUrl +
      separator +
      encodeURIComponent(paramName) +
      '=' +
      encodeURIComponent(paramValue) +
      hash
    )
  }
}

/**
 * 创建 GA 追踪器实例（单例模式）
 * 防止 React StrictMode 或多次渲染导致重复初始化
 */
export function createGATracker(options: CreateGATrackerOptions = {}): GATrackingEngine {
  // 如果已有全局实例且已初始化，直接返回
  if (globalEngineInstance) {
    return globalEngineInstance
  }

  globalEngineInstance = new GATrackingEngine(options.config, options.context)
  return globalEngineInstance
}

/**
 * 销毁全局追踪器实例
 */
export function destroyGATracker(): void {
  if (globalEngineInstance) {
    globalEngineInstance.destroy()
    globalEngineInstance = null
  }
}
